import { ItemOfferPrice } from "@/components/home";
import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";
import { Button, Card, Icon, InteractiveCard, Text } from "@/components/ui";
import { Plus } from "@/icons";
import type { RenderableSuggestion } from "@/domain/suggestions";
import { useMediaQuery } from "@/hooks/use-media-query";
import { emitCartBump, flyToCart, FLY_ORIGIN_ATTR } from "@/lib/fly-to-cart";
import { suggestionMessage } from "@/lib/i18n/suggestions-messages";

const IMAGE_SIZES = "144px";

export function SuggestionCard({
  suggestion,
  onAdd,
  onOpenDetail,
}: {
  suggestion: RenderableSuggestion;
  /** One-tap add — only called when `suggestion.oneTapAddable`. */
  onAdd: (event: React.MouseEvent<HTMLButtonElement>, suggestion: RenderableSuggestion) => void;
  /** Navigate to item detail — used when the offer needs required options. */
  onOpenDetail: (suggestion: RenderableSuggestion) => void;
}) {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    event.preventDefault();
    if (suggestion.oneTapAddable) {
      if (!prefersReducedMotion) {
        flyToCart(event.currentTarget.closest(`[${FLY_ORIGIN_ATTR}]`));
      }
      onAdd(event, suggestion);
      emitCartBump("add");
    } else {
      onOpenDetail(suggestion);
    }
  }

  return (
    <Card
      as="li"
      padding="xs"
      className="flex w-suggestion shrink-0 snap-start flex-col"
      data-testid={`suggestion-card-${suggestion.merchantItemId}`}
    >
      <div className="relative w-full aspect-square" {...{ [FLY_ORIGIN_ATTR]: "" }}>
        <InteractiveCard
          variant="plain"
          padding="none"
          onClick={() => onOpenDetail(suggestion)}
          className="absolute inset-0 overflow-hidden"
          aria-label={suggestion.name}
        >
          {suggestion.imageUrl ? (
            <Image
              width={200}
              height={200}
              alt={suggestion.name}
              src={suggestion.imageUrl}
              sizes={IMAGE_SIZES}
              fit="contain"
              className="h-full w-full"
            />
          ) : (
            <NoImage width={200} height={200} className="h-full w-full object-cover" />
          )}
        </InteractiveCard>
        <div className="absolute bottom-1 right-1">
          <Button
            variant="surface"
            tone="secondary"
            size="icon-md"
            elevation="raised"
            onClick={handleClick}
            aria-label={suggestionMessage("addAriaLabel").replace("{name}", suggestion.name)}
            data-testid={`suggestion-add-${suggestion.merchantItemId}`}
          >
            <Icon as={Plus} size="md" weight="bold" tone="secondary" />
          </Button>
        </div>
      </div>

      <InteractiveCard
        variant="plain"
        padding="none"
        onClick={() => onOpenDetail(suggestion)}
        tabIndex={-1}
        className="mt-2 flex flex-1 flex-col"
      >
        <Text clamp={2} weight="bold" leading="tight" className="min-h-10">
          {suggestion.name}
        </Text>
        <ItemOfferPrice price={suggestion.price} size="compact" />
      </InteractiveCard>
    </Card>
  );
}
